import { useEffect, useState } from 'react';

/**
 * Per-device age band selection (5-8 vs 9-12).
 *
 * Storage:
 *   - `explore-age-band-v1`         : "5-8" | "9-12"
 *   - `explore-age-band-picked-v1`  : "1" once the user has chosen (or dismissed) the picker
 *
 * No backend. Pure localStorage so it works offline and respects the
 * "no remote profile required" stance.
 */

/** @typedef {'5-8' | '9-12'} AgeBand */

const VALUE_KEY = 'explore-age-band-v1';
const PICKED_KEY = 'explore-age-band-picked-v1';

/** @type {AgeBand} */
const DEFAULT_BAND = '5-8';

/** @type {AgeBand[]} */
export const AGE_BANDS = ['5-8', '9-12'];

/** @param {unknown} v */
function isValid(v) {
  return v === '5-8' || v === '9-12';
}

const listeners = new Set();
function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore listener errors
    }
  });
}

/** @returns {AgeBand} */
export function getAgeBand() {
  if (typeof window === 'undefined') return DEFAULT_BAND;
  try {
    const v = window.localStorage.getItem(VALUE_KEY);
    return isValid(v) ? v : DEFAULT_BAND;
  } catch {
    return DEFAULT_BAND;
  }
}

/** @returns {boolean} true if the user has explicitly set or dismissed the picker */
export function hasPickedAgeBand() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PICKED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Persist a chosen band. Marks the picker as resolved.
 * @param {AgeBand} band
 */
export function setAgeBand(band) {
  if (!isValid(band)) return;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VALUE_KEY, band);
    window.localStorage.setItem(PICKED_KEY, '1');
  } catch {
    // ignore quota errors
  }
  emit();
}

/**
 * Mark the picker as dismissed without changing the band (keeps default).
 * Used by "Maybe later".
 */
export function dismissAgeBandPicker() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PICKED_KEY, '1');
  } catch {
    // ignore
  }
  emit();
}

/**
 * Reset the picker so it shows again. Useful from Settings.
 */
export function resetAgeBandPicker() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PICKED_KEY);
  } catch {
    // ignore
  }
  emit();
}

/** @param {() => void} cb */
export function subscribeAgeBand(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * React hook returning [band, setBand, hasPicked].
 * Re-renders when any consumer calls setAgeBand / dismiss / reset.
 */
export function useAgeBand() {
  const [state, setState] = useState(() => ({
    band: getAgeBand(),
    hasPicked: hasPickedAgeBand(),
  }));
  useEffect(() => {
    const unsub = subscribeAgeBand(() =>
      setState({ band: getAgeBand(), hasPicked: hasPickedAgeBand() }),
    );
    return () => unsub();
  }, []);
  return [state.band, setAgeBand, state.hasPicked];
}

/**
 * Display label, e.g. "Ages 5–8".
 * @param {AgeBand | string | null | undefined} band
 */
export function ageBandLabel(band) {
  if (band === '5-8' || band === '5\u20138') return 'Ages 5\u20138';
  if (band === '9-12' || band === '9\u201312') return 'Ages 9\u201312';
  return '';
}

/**
 * Normalize an authored ageRange (which may use `5-8`, `5\u20138`, `5–8`) to canonical AgeBand.
 * Returns null if it can't be classified.
 * @param {string | null | undefined} rangeRaw
 * @returns {AgeBand | null}
 */
export function normalizeAgeRange(rangeRaw) {
  if (!rangeRaw) return null;
  const r = String(rangeRaw).replace(/[\u2010-\u2015]/g, '-');
  if (r === '5-8') return '5-8';
  if (r === '9-12') return '9-12';
  return null;
}

/**
 * Sort comparator: paths matching the active band come first, ties keep input order.
 * @param {AgeBand} activeBand
 */
export function compareByAgeBandMatch(activeBand) {
  return (a, b) => {
    const am = normalizeAgeRange(a?.ageBand) === activeBand ? 0 : 1;
    const bm = normalizeAgeRange(b?.ageBand) === activeBand ? 0 : 1;
    return am - bm;
  };
}
